import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  IHaulerSearchViewModel,
  IHaulerViewModel,
  ITruckSearchViewModel,
  TruckSearchViewModel,
  VehicleType
} from '@services/cleanfill-api/cleanfill-api.generated';
import { HaulerMapperService } from '@services/cleanfill-core/haulers/hauler-mapper.service';
import { StoreHaulersService } from '@services/cleanfill-core/haulers/store-haulers.service';
import { PortalState } from '@view-models/enums/portal-state';
import { SearchQuery } from '@view-models/search-query/search-query';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BatchDetailStore } from '../../batch-detail.store';

enum PopulateFields {
  TruckNumber = 'Truck_Number',
  TruckPlate = 'Truck_Plate',
  Hauler = 'Hauler',
  TruckType = 'Truck_Type',
  DriverName = 'Driver_Name',
  ALL = 'ALL',
}

interface ShowOther {
  hauler: boolean;
  vehicleId: boolean;
  vehiclePlate: boolean;
}

@Component({
  selector: 'app-esvr-transport-information',
  templateUrl: './esvr-transport-information.component.html',
  styleUrls: ['./esvr-transport-information.component.scss'],
})
export class EsvrTransportInformationComponent implements OnInit, OnDestroy {
  static stVehicleWeight: number;

  readonly PopulateFields = PopulateFields;
  readonly Other = 'Other';
  readonly OtherOption = { name: this.Other };

  subscriptions = new Subscription();
  form: FormGroup;
  isSourceSiteUser: boolean;
  isFillSiteUser: boolean;
  showOther: ShowOther;
  truckLists: Array<ITruckSearchViewModel> = [];
  // hauler
  haulerControl = new FormControl();
  haulerList: IHaulerSearchViewModel[];
  filteredHaulerOptions: IHaulerSearchViewModel[];
  // vehicle types
  vehicleTypeFormArray: { name: string; value: VehicleType }[];

  @Input() portalState: PortalState;
  @Input() submited: boolean;
  @Input() editing: boolean;

  get formGroup() {
    return this.formRef ? this.formRef.control : null;
  }

  get loads(): FormArray {
    return this.form?.get('loads') as FormArray;
  }

  get loadsToBeProcessed(): number {
    return this.loads?.controls.reduce((a, c) => {
      const entry = Number(c.get('count').value) || 0;
      if (entry > 0 && entry % 1 === 0) {
        return a + entry;
      }
      return a;
    }, 0);
  }

  constructor(
    public formRef: ControlContainer,
    public haulerMapper: HaulerMapperService,
    public haulerStore: StoreHaulersService,
    private batchDetail: BatchDetailStore
  ) {
    this.vehicleTypeFormArray = Object.entries(VehicleType)
      .map(([name, value]) => ({ name, value }))
      .map((vehicleType) => (vehicleType = { name: vehicleType.name.match(/[A-Z][a-z]+|[0-9]+/g).join(' '), value: vehicleType.value }));
    this.showOther = { hauler: false, vehicleId: false, vehiclePlate: false };
  }

  ngOnInit(): void {
    this.isSourceSiteUser = this.portalState === PortalState.SOURCE_SITE;
    this.isFillSiteUser = this.portalState === PortalState.FILL_SITE;

    this.subscriptions.add(this.haulerStore.haulerList$.subscribe((haulers) => {
      this.repopulateHaulers(haulers);
    }));

    this.subscriptions.add(this.haulerControl.valueChanges.subscribe((value => {
      const filterValue = typeof value === 'string' ? value : value.name;
      if (filterValue) {
        this.filteredHaulerOptions = this.haulerList.filter(hauler => hauler.name.toLowerCase().includes(filterValue.toLowerCase()));
      }
      else {
        this.filteredHaulerOptions = this.haulerList.slice();
      }
    })));

    this.haulerStore.loadHauleres(
      new SearchQuery()
        .setPageIndex(undefined)
        .setPageSize(undefined)
        .appendSortBy({ property: 'name' })
    );
  }

  getFieldValue(field: string) {
    const fieldValue = this.formGroup?.get(field).value;

    if (typeof fieldValue === 'string') {
      return fieldValue;
    }

    return fieldValue?.name;
  }

  markAsPrestine() {
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
  }

  async repopulate() {
    this.isSourceSiteUser = this.portalState === PortalState.SOURCE_SITE;
    const haulers = await this.haulerStore.haulerList$.pipe(take(1)).toPromise();
    this.repopulateHaulers(haulers);
  }

  repopulateHaulers(haulers: IHaulerSearchViewModel[]) {
    this.haulerList = haulers;
    this.filteredHaulerOptions = haulers;

    const enteredHauler = this.formGroup.get('hauler').value;
    if (enteredHauler) {
      const hauler = haulers.find((haul) => haul.name === enteredHauler);
      if (hauler) {
        this.chooseHauler(hauler);
        this.formGroup.get('hauler').setValue(hauler);
        this.haulerControl.setValue(hauler);
        this.showOther.hauler = false;
      } else {
        this.haulerControl.setValue(this.OtherOption);
        this.showOther.hauler = true;
        this.checkExistenceOfTruck();
      }
    } else {
      this.showOther.hauler = false;
      this.haulerControl.setValue('');
    }
  }

  checkExistenceOfTruck() {
    const truckNumber = this.formGroup.get('truckNumber').value;
    const vehiclePlate = this.formGroup.get('vehiclePlate').value;
    const vehicleType = this.formGroup.get('vehicleType').value;
    if (truckNumber) {
      const truckNumberExist = this.truckLists.find((truck) => {
        if (truck.vehicleWeight) EsvrTransportInformationComponent.stVehicleWeight = truck.vehicleWeight;
        return truck.truckNumber === truckNumber;
      });
      this.showOther.vehicleId = truckNumberExist ? false : true;
    }
    if (vehiclePlate) {
      const vehiclePlateExist = this.truckLists.find((truck) => {
        if (truck.vehicleWeight) EsvrTransportInformationComponent.stVehicleWeight = truck.vehicleWeight;
        return truck.vehiclePlate === vehiclePlate;
      });
      this.showOther.vehiclePlate = vehiclePlateExist ? false : true;
    }
  }

  chooseHauler(option: IHaulerSearchViewModel) {
    this.showOther.hauler = option.uniqueId === undefined;
    this.resetFields([
      PopulateFields.TruckType,
      PopulateFields.TruckNumber,
      PopulateFields.TruckPlate,
      PopulateFields.DriverName,
    ]);
    if (option.uniqueId) {
      this.formGroup.get('hauler').setValue(option);
      this.queryNewTruckList(option.uniqueId, null);
    } else {
      this.formGroup.get('hauler').setValue('');
    }
    this.markAsPrestine();
  }

  elementIsInTruckList(field: PopulateFields, val) {
    return this.truckLists.some((el) => {
      if (field === PopulateFields.TruckNumber) {
        return el.truckNumber === val;
      } else if (field === PopulateFields.TruckPlate) {
        return el.vehiclePlate === val;
      }
    });
  }

  chooseVehicleType(val: VehicleType) {
    this.batchDetail.esvrSubject.next({ vehicleType: val });
    const hauler = this.formGroup.get('hauler').value as IHaulerViewModel;
    this.resetFields([PopulateFields.TruckNumber, PopulateFields.TruckPlate]);
    if (val && hauler.uniqueId) {
      this.queryNewTruckList(hauler.uniqueId, val);
    }
  }

  resetFields(fields: PopulateFields[]) {
    if (!this.editing) {
      return;
    }
    if (fields.includes(PopulateFields.TruckType) || fields.includes(PopulateFields.ALL)) {
      (this.formGroup as FormGroup).controls.vehicleType.setValue(undefined);
    }
    if (fields.includes(PopulateFields.TruckNumber) || fields.includes(PopulateFields.ALL)) {
      (this.formGroup as FormGroup).controls.truckNumber.setValue('');
      this.showOther.vehicleId = this.showOther.hauler;
    }
    if (fields.includes(PopulateFields.TruckPlate) || fields.includes(PopulateFields.ALL)) {
      (this.formGroup as FormGroup).controls.vehiclePlate.setValue('');
      this.showOther.vehiclePlate = this.showOther.hauler;
    }
    if (fields.includes(PopulateFields.DriverName)) {
      (this.formGroup as FormGroup).controls.driver.setValue('');
    }
  }

  queryNewTruckList(haulerId: string, vt: VehicleType) {
    const query = new SearchQuery().setPageIndex(undefined).setPageSize(undefined);
    query.appendFilterBy({ property: 'haulerid', equal: haulerId });
    this.haulerStore.getTrucksList(query).then((trucks) => {
      if (vt) {
        this.truckLists = trucks.filter((truck) => {
          return truck.vehicleType === vt;
        });
      } else {
        this.truckLists = trucks;
      }
      if (this.truckLists.length === 0) {
        this.showOther.vehicleId = true;
        this.showOther.vehiclePlate = true;
      }
      this.checkExistenceOfTruck();
    });
  }

  populateField(field: PopulateFields, option: TruckSearchViewModel) {
    if (field === PopulateFields.TruckNumber) {
      (this.formGroup as FormGroup).controls.truckNumber.setValue(option.truckNumber);
      (this.formGroup as FormGroup).controls.vehicleType.setValue(option.vehicleType);
      (this.formGroup as FormGroup).controls.driver.setValue(option.driverName);
    }
    if (field === PopulateFields.TruckPlate) {
      (this.formGroup as FormGroup).controls.vehiclePlate.setValue(option.vehiclePlate);
      (this.formGroup as FormGroup).controls.vehicleType.setValue(option.vehicleType);
      (this.formGroup as FormGroup).controls.driver.setValue(option.driverName);
    }
    if (option.vehicleType) {
      this.batchDetail.esvrSubject.next({ vehicleType: option.vehicleType });
    }
    this.checkExistenceOfTruck();
  }

  displayHauler(hauler: IHaulerSearchViewModel): string {
    return hauler?.name ?? '';
  }

  resetHaulerSelect(trigger: MatAutocompleteTrigger) {
    this.filteredHaulerOptions = this.haulerList;
    trigger.openPanel()
  }

  updateHaulerSelect() {
    const enteredHauler = this.haulerControl.value;
    if (typeof (enteredHauler) !== 'string') {
      return;
    }
    const hauler = this.haulerList.find((haul) => haul.name.toLowerCase() === enteredHauler.toLowerCase());
    if (hauler) {
      this.haulerControl.setValue(hauler);
      this.showOther.hauler = false;
    }
    else {
      this.haulerControl.setValue(this.OtherOption);
      this.showOther.hauler = true;
      this.formGroup.get('hauler').setValue(enteredHauler);
      this.truckLists = [];
    }
    this.resetFields([
      PopulateFields.TruckType,
      PopulateFields.TruckNumber,
      PopulateFields.TruckPlate,
      PopulateFields.DriverName
    ]);
    this.markAsPrestine();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.haulerStore.clearHauleres();
    this.haulerStore.clearTrucks();
  }
}
